'use client';

import React, { createContext, useContext, useRef } from 'react';
import { Span, context as OTelContext, trace, SpanOptions } from '@dealroadshow/jsbro/OTel/api';
import { getContainer } from '@framework/di/getContainer';

export type IUseOTelTraceContext = ReturnType<typeof useOTelTrace>;

// @ts-ignore TODO: FIN-32798 strictNullChecks
export const OTelTraceContext = createContext<IUseOTelTraceContext>(null);
export const OTelTraceContextSymbol = Symbol('OTelTraceContext');

// sometimes there is no parent context, so we need to skip the check
export const useOTelTraceContext = (skipCheck = false): IUseOTelTraceContext => {
  const context = useContext<IUseOTelTraceContext>(OTelTraceContext);
  if (!skipCheck && !context) {
    throw new Error('useOTelTraceContext must be used within a OTelTraceContextProvider');
  }
  return context;
};

const useOTelTrace = (
  name: string,
  options?: SpanOptions,
  context?: { span: Span },
) => {
  const spanRef = useRef<Span>(null);

  if (!spanRef.current) {
    const tracer = trace.getTracer();
    let ctx = OTelContext.active();
    if (context) {
      // @ts-ignore
      ctx = trace.setSpan(OTelContext.active(), context.span);
    }

    // @ts-ignore
    spanRef.current = tracer.startSpan(name, options, ctx);
  }

  return {
    span: spanRef.current,
  };
};

export interface IProps {
  children: React.ReactNode,
  name?: string,
  options?: SpanOptions,
  context?: IUseOTelTraceContext,
}

export const getName = (children: any) => {
  const getDisplayName = (element: React.ReactNode): string => {
    let defaultName = 'ReactNode';

    if (React.isValidElement(element)) {
      return (
        // @ts-ignore
        element.type?.displayName ||
        // @ts-ignore
        element.type?.name ||
        element.constructor?.name ||
        defaultName
      );
    }
    return defaultName;
  };

  if (Array.isArray(children)) {
    return children.map(getDisplayName)
      .join(', ');
  }
  return getDisplayName(children);
};

const OTelTraceContextProvider = ({
  children,
  name,
  options,
  context,
}: IProps) => {
  if (!name) {
    name = getName(children);
  }
  const contextValue = useOTelTrace(name, options, context);
  const DIContext = getContainer();
  DIContext.bindContextValue(OTelTraceContextSymbol, contextValue, true);

  return (
    <OTelTraceContext.Provider value={ contextValue }>
      { children }
    </OTelTraceContext.Provider>
  );
};

export default OTelTraceContextProvider;
